import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Sparkles,
  Globe,
  Server,
  Loader2,
  Info,
  Plus,
  X,
  UserCog,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import { useAuthStore } from '@/stores/auth-store';
import { cn } from '@/lib/utils';
import type { WizardState } from './useWizardState';

type PreflightStatus =
  | 'in_account'
  | 'subdomain_ok'
  | 'not_in_account'
  | 'unregistered'
  | 'cf_unconfigured'
  | 'invalid_domain';

interface PreflightResult {
  domain: string;
  status: PreflightStatus;
  zone_id?: string;
  zone_status?: string;
  authoritative_ns?: string[];
  recommendation: string;
}

interface StepProps {
  state: WizardState;
  updateState: (updates: Partial<WizardState>) => void;
}

const COMMON_SUFFIXES = /\b(co\.?|inc\.?|llc\.?|ltd\.?|corp\.?|corporation|company|enterprises?)\b/gi;

function generateSiteId(name: string): string {
  return name
    .toLowerCase()
    .replace(COMMON_SUFFIXES, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function generateShortName(name: string): string {
  const cleaned = name.replace(COMMON_SUFFIXES, '').trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length <= 2) return cleaned;
  return words.slice(0, Math.min(words.length - 1, 3)).join(' ');
}

/**
 * Renders the result of the cross-account Cloudflare preflight check next to
 * the custom-domain input. The pre-existing static "DNS Configuration
 * Required" blue info box was misleading for tenants whose domain lives in
 * a Cloudflare account we don't control — the wizard couldn't actually
 * automate DNS for them and silently shipped a broken site. This banner is
 * driven by /admin/platform/site-wizard/cf-preflight so the operator sees
 * the real situation before committing.
 */
function PreflightBanner({
  preflight,
  pending,
  domain,
}: {
  preflight: PreflightResult | null;
  pending: boolean;
  domain: string;
}) {
  if (!domain) {
    return (
      <div className="flex items-start gap-2 p-3 bg-stone-50 border border-stone-200 rounded-lg text-xs text-text-muted">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Enter a domain to check its DNS configuration.</span>
      </div>
    );
  }
  if (pending) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex items-center gap-2 p-3 bg-stone-50 border border-stone-200 rounded-lg text-xs text-text-muted"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Checking Cloudflare configuration for {domain}…</span>
      </div>
    );
  }
  if (!preflight) {
    return null;
  }

  const tone =
    preflight.status === 'in_account' || preflight.status === 'subdomain_ok'
      ? 'success'
      : preflight.status === 'not_in_account' || preflight.status === 'cf_unconfigured'
      ? 'warn'
      : 'error';

  const Icon =
    tone === 'success' ? CheckCircle2 : tone === 'warn' ? AlertTriangle : XCircle;

  const toneClasses =
    tone === 'success'
      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
      : tone === 'warn'
      ? 'bg-amber-50 border-amber-200 text-amber-900'
      : 'bg-rose-50 border-rose-200 text-rose-900';

  const title =
    preflight.status === 'in_account'
      ? 'Domain is in our Cloudflare account'
      : preflight.status === 'subdomain_ok'
      ? 'Rumio subdomain — no DNS setup needed'
      : preflight.status === 'not_in_account'
      ? "Domain isn't in our Cloudflare account"
      : preflight.status === 'unregistered'
      ? 'Domain has no public nameservers'
      : preflight.status === 'cf_unconfigured'
      ? 'Cloudflare API not configured'
      : 'Domain format is invalid';

  return (
    <div
      role={tone === 'success' ? 'status' : 'alert'}
      className={cn(
        'flex items-start gap-2 p-3 border rounded-lg text-sm',
        toneClasses,
      )}
    >
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        <p className="text-xs opacity-90">{preflight.recommendation}</p>
        {preflight.authoritative_ns && preflight.authoritative_ns.length > 0 && (
          <p className="text-xs opacity-90">
            Authoritative NS:{' '}
            <code className="font-mono">
              {preflight.authoritative_ns.join(', ')}
            </code>
          </p>
        )}
        {preflight.zone_status && preflight.zone_status !== 'active' && (
          <p className="text-xs opacity-90">
            Zone status:{' '}
            <code className="font-mono">{preflight.zone_status}</code>
          </p>
        )}
      </div>
    </div>
  );
}

export default function WizardStep1Identity({ state, updateState }: StepProps) {
  const operatorUser = useAuthStore((s) => s.user);
  const [isGeneratingTaglines, setIsGeneratingTaglines] = useState(false);
  const [suggestedTaglines, setSuggestedTaglines] = useState<{ text: string; reasoning?: string }[]>([]);
  const [newAliasDomain, setNewAliasDomain] = useState('');
  const lastAutoGenName = useRef('');
  const [preflight, setPreflight] = useState<PreflightResult | null>(null);
  const [preflightPending, setPreflightPending] = useState(false);
  const [domainType, setDomainType] = useState<'subdomain' | 'custom'>(
    state.frontendDomain && !state.frontendDomain.endsWith('.rumio.world')
      ? 'custom'
      : 'subdomain'
  );

  const inputClass =
    'w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500';
  const labelClass = 'block text-sm font-medium text-text-primary mb-1';

  const handleStoreNameChange = useCallback((name: string) => {
    updateState({ storeName: name });

    // Auto-regenerate siteId and shortName whenever the store name changes
    const trimmed = name.trim();
    if (trimmed) {
      const newSiteId = generateSiteId(trimmed);
      const newShortName = generateShortName(trimmed);
      const updates: Partial<WizardState> = {
        siteId: newSiteId,
        shortName: newShortName,
      };
      // Also update subdomain if using subdomain mode
      if (domainType === 'subdomain') {
        updates.frontendDomain = `${newSiteId}.rumio.world`;
        updates.apiDomain = `api.${newSiteId}.rumio.world`;
      }
      updateState(updates);
      lastAutoGenName.current = trimmed;
    }
  }, [domainType, updateState]);

  const handleSuggestTaglines = async () => {
    if (!state.storeName || !state.businessDescription) return;
    setIsGeneratingTaglines(true);
    setSuggestedTaglines([]);

    try {
      const response = await api.post<any>(
        '/admin/ai/generate/wizard-assist',
        {
          action: 'suggest-taglines',
          context: {
            storeName: state.storeName,
            businessDescription: state.businessDescription,
          },
        }
      );

      // Handle both formats: [{ text, reasoning }] or ["string"]
      const raw = response.taglines || response.result?.taglines || [];
      const parsed = raw.map((t: any) => {
        if (typeof t === 'string') return { text: t };
        return { text: t.text || t.tagline || '', reasoning: t.reasoning || '' };
      }).filter((t: any) => t.text);

      setSuggestedTaglines(parsed);
    } catch (err) {
      console.error('Tagline suggestion failed:', err);
      setSuggestedTaglines([]);
    } finally {
      setIsGeneratingTaglines(false);
    }
  };

  const getAliasDomains = (): string[] => {
    try {
      const stored = (state as any).aliasDomains;
      if (Array.isArray(stored)) return stored;
    } catch {}
    return [];
  };

  const addAliasDomain = () => {
    const domain = newAliasDomain.trim().replace(/^https?:\/\//, '');
    if (!domain) return;
    const current = getAliasDomains();
    if (!current.includes(domain)) {
      updateState({ aliasDomains: [...current, domain] } as any);
    }
    setNewAliasDomain('');
  };

  const removeAliasDomain = (domain: string) => {
    const current = getAliasDomains();
    updateState({ aliasDomains: current.filter((d: string) => d !== domain) } as any);
  };

  const siteId = state.siteId || '';
  const subdomainPreview = `${siteId || 'your-site'}.rumio.world`;
  const customDomain = state.frontendDomain || '';

  // Debounced Cloudflare preflight check. Runs only when the user is on the
  // custom-domain path with a non-empty, well-formed-looking domain. Catches
  // the cross-account-CF case (domain lives in the customer's CF account, not
  // ours) before site creation silently skips DNS automation — same scenario
  // that bit threeonethreestandard on 2026-05-12.
  useEffect(() => {
    if (domainType !== 'custom') {
      setPreflight(null);
      setPreflightPending(false);
      return;
    }
    const trimmed = customDomain.trim();
    // Cheap shape check — backend re-validates with the canonical regex. We
    // only suppress the spinner for obvious non-domains while typing.
    if (!trimmed || !/\.[a-z]{2,}$/i.test(trimmed)) {
      setPreflight(null);
      setPreflightPending(false);
      return;
    }
    setPreflightPending(true);
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await api.post<PreflightResult>(
          '/admin/platform/site-wizard/cf-preflight',
          { domain: trimmed },
        );
        if (!cancelled) setPreflight(res);
      } catch {
        // Network/auth blip — keep silent. The wizard will still run; this
        // banner is informational only.
        if (!cancelled) setPreflight(null);
      } finally {
        if (!cancelled) setPreflightPending(false);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [customDomain, domainType]);
  const derivedApiDomain =
    domainType === 'custom' && customDomain
      ? `api.${customDomain}`
      : `api.${subdomainPreview}`;

  const siteType = state.siteType || 'ecommerce';

  return (
    <div className="space-y-6">
      {/* Site Type */}
      <div>
        <label className={labelClass}>Site Type</label>
        <div
          role="radiogroup"
          aria-label="Site type"
          className="grid grid-cols-2 lg:grid-cols-5 gap-3"
        >
          <button
            type="button"
            role="radio"
            aria-checked={siteType === 'ecommerce'}
            onClick={() => updateState({ siteType: 'ecommerce' })}
            className={cn(
              'text-left rounded-lg border-2 p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600',
              siteType === 'ecommerce'
                ? 'border-sage-600 bg-sage-50'
                : 'border-stone-300 hover:border-sage-400'
            )}
          >
            <div className="font-medium text-text-primary">Ecommerce store</div>
            <p className="text-xs text-text-muted mt-1">
              Products, cart, checkout, inventory. Default for selling physical
              goods or services.
            </p>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={siteType === 'saas'}
            onClick={() => updateState({ siteType: 'saas' })}
            className={cn(
              'text-left rounded-lg border-2 p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600',
              siteType === 'saas'
                ? 'border-sage-600 bg-sage-50'
                : 'border-stone-300 hover:border-sage-400'
            )}
          >
            <div className="font-medium text-text-primary">SaaS marketing site</div>
            <p className="text-xs text-text-muted mt-1">
              Marketing site for a platform product. Skips products; seeds
              Features / Customers / About pages.
            </p>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={siteType === 'museum'}
            onClick={() => updateState({ siteType: 'museum' })}
            className={cn(
              'text-left rounded-lg border-2 p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600',
              siteType === 'museum'
                ? 'border-sage-600 bg-sage-50'
                : 'border-stone-300 hover:border-sage-400'
            )}
          >
            <div className="font-medium text-text-primary">Museum / nonprofit</div>
            <p className="text-xs text-text-muted mt-1">
              Cultural institution. Auto-enables Penny fundraising, scaffolds
              Visit / Exhibits / Membership / Donate. Gift shop is opt-in.
            </p>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={siteType === 'lodging'}
            onClick={() => updateState({ siteType: 'lodging' })}
            className={cn(
              'text-left rounded-lg border-2 p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600',
              siteType === 'lodging'
                ? 'border-sage-600 bg-sage-50'
                : 'border-stone-300 hover:border-sage-400'
            )}
          >
            <div className="font-medium text-text-primary">Short-term rental</div>
            <p className="text-xs text-text-muted mt-1">
              Direct-booking site for cabins / cottages / vacation homes.
              Properties + bookings + iCal channel sync. No cart / checkout.
            </p>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={siteType === 'social_casino'}
            onClick={() => updateState({ siteType: 'social_casino' })}
            className={cn(
              'text-left rounded-lg border-2 p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600',
              siteType === 'social_casino'
                ? 'border-sage-600 bg-sage-50'
                : 'border-stone-300 hover:border-sage-400'
            )}
          >
            <div className="font-medium text-text-primary">Social casino / slots</div>
            <p className="text-xs text-text-muted mt-1">
              Marketing shell for an embedded slot/casino-style game app.
              No cart, no products — purchases happen inside the app.
            </p>
          </button>
        </div>
      </div>

      {/* Ownership */}
      <div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={state.ownership === 'customer'}
            onChange={(e) =>
              updateState({ ownership: e.target.checked ? 'customer' : 'platform' })
            }
            className="mt-1 h-4 w-4 rounded border-stone-300 text-sage-600 focus:ring-sage-600"
          />
          <span>
            <span className="font-medium text-text-primary">
              This site is for an external customer
            </span>
            <span className="block text-xs text-text-muted mt-1">
              Check this when you're creating a tenant for someone else (not for
              yourself). The platform owner won't be seeded into the new tenant's
              admin user list, so your customer won't see an unfamiliar account.
              You'll still have full super-admin access on the tenant.
            </span>
          </span>
        </label>
      </div>

      {/* Store / Site / Museum / Lodging Name */}
      <div>
        <label className={labelClass}>
          {siteType === 'saas'
            ? 'Site Name'
            : siteType === 'museum'
            ? 'Museum / Organization Name'
            : siteType === 'lodging'
            ? 'Brand / Property Name'
            : siteType === 'social_casino'
            ? 'Casino / Game Brand Name'
            : 'Store Name'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <input
          className={inputClass}
          value={state.storeName || ''}
          onChange={(e) => handleStoreNameChange(e.target.value)}
          placeholder="The Ledger Lab Academy"
          required
        />
        <p className="text-xs text-text-muted mt-1">
          Your store's display name. Site ID and short name auto-update as you type.
        </p>
      </div>

      {/* Site ID & Short Name row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Site ID</label>
          <input
            className={inputClass}
            value={state.siteId || ''}
            onChange={(e) => {
              const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
              updateState({ siteId: cleaned });
            }}
            placeholder="ledger-lab-academy"
          />
          <p className="text-xs text-text-muted mt-1">
            <code className="bg-stone-100 px-1 py-0.5 rounded text-xs">
              sites/{siteId || '...'}
            </code>
          </p>
        </div>
        <div>
          <label className={labelClass}>Short Name</label>
          <input
            className={inputClass}
            value={state.shortName || ''}
            onChange={(e) => updateState({ shortName: e.target.value })}
            placeholder="Ledger Lab"
          />
        </div>
      </div>

      {/* Business Description */}
      <div>
        <label className={labelClass}>
          {siteType === 'museum'
            ? 'Tell me about your museum'
            : siteType === 'lodging'
            ? 'Tell me about your rental(s)'
            : siteType === 'social_casino'
            ? 'Tell me about your slot game'
            : 'Tell me about your business'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <textarea
          className={cn(inputClass, 'resize-none')}
          rows={4}
          value={state.businessDescription || ''}
          onChange={(e) => updateState({ businessDescription: e.target.value })}
          placeholder={
            siteType === 'museum'
              ? "A small historical museum in southeast Michigan dedicated to preserving the stories of immigrants, workers, and neighborhoods..."
              : siteType === 'lodging'
              ? "A pair of lakefront cabins on Pictured Rocks National Lakeshore. Off-grid in the best way — wood stove, kayak from the dock, dark-sky stargazing. Owner-managed, direct booking only..."
              : siteType === 'social_casino'
              ? "A Vegas-style social slot game with daily bonuses, weekly tournaments, and themed reels. Play-for-fun only — coins and gems are virtual, never redeemable for cash..."
              : "We're an online platform that teaches accounting and bookkeeping to beginners through advanced professionals..."
          }
        />
      </div>

      {/* Museum-specific fields. Drives Penny org_profile pre-population
          (NTEE A50 + tax status), the museum content generators (collection
          focus prompt context), and the gift-shop opt-in (re-enables the
          ecommerce flag + /shop routes). */}
      {siteType === 'museum' && (
        <div className="space-y-4 rounded-lg border-2 border-sage-200 bg-sage-50/40 p-4">
          <div className="text-sm font-semibold text-sage-800">Museum details</div>

          <div>
            <label className={labelClass}>Collection focus / notable exhibits</label>
            <textarea
              className={cn(inputClass, 'resize-none')}
              rows={2}
              value={state.museumCollectionFocus || ''}
              onChange={(e) => updateState({ museumCollectionFocus: e.target.value })}
              placeholder="Local industrial history, immigrant communities, oral histories from longtime residents..."
            />
            <p className="text-xs text-text-muted mt-1">
              Used as context when generating exhibit blog posts and the About / Mission pages.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>City</label>
              <input
                className={inputClass}
                value={state.museumCity || ''}
                onChange={(e) => updateState({ museumCity: e.target.value })}
                placeholder="Hamtramck"
              />
            </div>
            <div>
              <label className={labelClass}>State / Region</label>
              <input
                className={inputClass}
                value={state.museumRegion || ''}
                onChange={(e) => updateState({ museumRegion: e.target.value })}
                placeholder="MI"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Tax status</label>
            <div role="radiogroup" aria-label="Tax status" className="grid grid-cols-3 gap-2">
              {(['501c3', '501c6', 'for_profit'] as const).map((status) => (
                <button
                  key={status}
                  type="button"
                  role="radio"
                  aria-checked={state.museumTaxStatus === status}
                  onClick={() => updateState({ museumTaxStatus: status })}
                  className={cn(
                    'rounded-md border-2 px-3 py-2 text-sm transition-colors',
                    state.museumTaxStatus === status
                      ? 'border-sage-600 bg-sage-50'
                      : 'border-stone-300 hover:border-sage-400'
                  )}
                >
                  {status === '501c3' ? '501(c)(3) — tax-deductible' : status === '501c6' ? '501(c)(6)' : 'For-profit'}
                </button>
              ))}
            </div>
            <p className="text-xs text-text-muted mt-1">
              501(c)(3) enables tax-deductibility language in the donation receipt policy.
            </p>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!state.museumHasGiftShop}
                onChange={(e) => updateState({ museumHasGiftShop: e.target.checked })}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-text-primary">Will this museum have a gift shop?</span>
                <span className="block text-xs text-text-muted mt-0.5">
                  Re-enables ecommerce features (cart, checkout, /shop). Off by default for museums.
                </span>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Lodging-specific fields. Drives the lodging content generators
          (market region context for area guide + blog), the booking-engine
          UX (single host vs. property manager), and policy generation
          (payment model controls cancellation tier copy). */}
      {siteType === 'lodging' && (
        <div className="space-y-4 rounded-lg border-2 border-sage-200 bg-sage-50/40 p-4">
          <div className="text-sm font-semibold text-sage-800">Rental details</div>

          <div>
            <label className={labelClass}>Market region</label>
            <input
              className={inputClass}
              value={state.lodgingMarketRegion || ''}
              onChange={(e) => updateState({ lodgingMarketRegion: e.target.value })}
              placeholder="Pictured Rocks National Lakeshore, Munising MI"
            />
            <p className="text-xs text-text-muted mt-1">
              Used as context for the area guide, things-to-do page, and blog posts.
            </p>
          </div>

          <div>
            <label className={labelClass}>Operating model</label>
            <div role="radiogroup" aria-label="Host type" className="grid grid-cols-2 gap-2">
              {(['single_host', 'property_manager'] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  role="radio"
                  aria-checked={state.lodgingHostType === kind}
                  onClick={() => updateState({ lodgingHostType: kind })}
                  className={cn(
                    'rounded-md border-2 px-3 py-2 text-sm transition-colors text-left',
                    state.lodgingHostType === kind
                      ? 'border-sage-600 bg-sage-50'
                      : 'border-stone-300 hover:border-sage-400'
                  )}
                >
                  <div className="font-medium">
                    {kind === 'single_host' ? 'Single host (1-5 properties)' : 'Property manager (many units)'}
                  </div>
                  <div className="text-xs text-text-muted mt-1">
                    {kind === 'single_host'
                      ? "Owner-host voice. Simpler admin UI."
                      : "Team voice. Built for multi-host fleet management."}
                  </div>
                </button>
              ))}
            </div>
            <p className="text-xs text-text-muted mt-1">
              Affects copy tone and which admin views are scaffolded. The schema supports both.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>How many properties? (approx)</label>
              <input
                type="number"
                min={1}
                className={inputClass}
                value={state.lodgingPropertyCount ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  updateState({ lodgingPropertyCount: v === '' ? undefined : Math.max(1, Number(v)) });
                }}
                placeholder="2"
              />
            </div>
            <div>
              <label className={labelClass}>Payment model</label>
              <select
                className={inputClass}
                value={state.lodgingPaymentModel || ''}
                onChange={(e) => updateState({ lodgingPaymentModel: (e.target.value || undefined) as any })}
              >
                <option value="">Pick one…</option>
                <option value="deposit_balance">Deposit at booking + balance 14d before</option>
                <option value="deposit_only">Deposit at booking; balance at check-in</option>
                <option value="full_upfront">Full payment at booking</option>
              </select>
            </div>
          </div>
          <p className="text-xs text-text-muted -mt-2">
            Drives the cancellation policy copy and the Stripe Payment Intent flow.
          </p>

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={
                state.lodgingSingleProperty ??
                (state.lodgingHostType === 'single_host' && state.lodgingPropertyCount === 1)
              }
              onChange={(e) => updateState({ lodgingSingleProperty: e.target.checked })}
              className="mt-1"
            />
            <span>
              <span className="font-medium text-text-primary">Single-property site</span>
              <span className="block text-xs text-text-muted mt-0.5">
                The homepage becomes the property itself — photo hero, gallery, and a live
                booking widget — with a persistent "Book Now" button and the marketing
                sections below. Best when the site markets one home. You can change this later.
              </span>
            </span>
          </label>
        </div>
      )}

      {/* Social-casino-specific fields. Drives the social-casino content
          generators (game style + theme prompts), the age-gate seeded
          min-age, and the optional app_embed homepage section. */}
      {siteType === 'social_casino' && (
        <div className="space-y-4 rounded-lg border-2 border-sage-200 bg-sage-50/40 p-4">
          <div className="text-sm font-semibold text-sage-800">Game details</div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Game style</label>
              <input
                className={inputClass}
                value={state.socialCasinoGameStyle || ''}
                onChange={(e) => updateState({ socialCasinoGameStyle: e.target.value })}
                placeholder="Vegas slots, bingo + slots, scratchers…"
              />
            </div>
            <div>
              <label className={labelClass}>Mascot / theme</label>
              <input
                className={inputClass}
                value={state.socialCasinoMascotOrTheme || ''}
                onChange={(e) => updateState({ socialCasinoMascotOrTheme: e.target.value })}
                placeholder="Neon arcade, tropical, classic Vegas…"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Minimum age</label>
              <input
                type="number"
                min={13}
                max={99}
                className={inputClass}
                value={state.socialCasinoMinAge ?? 18}
                onChange={(e) => {
                  const v = e.target.value;
                  updateState({ socialCasinoMinAge: v === '' ? undefined : Math.max(13, Number(v)) });
                }}
                placeholder="18"
              />
              <p className="text-xs text-text-muted mt-1">
                Drives the age-gate modal and the legal-page minimum-age copy.
              </p>
            </div>
            <div>
              <label className={labelClass}>Game app slug (optional)</label>
              <input
                className={inputClass}
                value={state.socialCasinoGameAppId || ''}
                onChange={(e) => updateState({ socialCasinoGameAppId: e.target.value })}
                placeholder="slotlandia-app"
              />
              <p className="text-xs text-text-muted mt-1">
                Apps Platform slug. When set, seeds an embedded-app homepage section.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tagline */}
      <div>
        <label className={labelClass}>Tagline</label>
        <div className="flex gap-2">
          <input
            className={cn(inputClass, 'flex-1')}
            value={state.tagline || ''}
            onChange={(e) => updateState({ tagline: e.target.value })}
            placeholder="Your catchy tagline here"
          />
          <Button
            variant="outline"
            size="md"
            onClick={handleSuggestTaglines}
            disabled={isGeneratingTaglines || !state.storeName || !state.businessDescription}
          >
            {isGeneratingTaglines ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            Suggest with AI
          </Button>
        </div>

        {/* Suggested taglines */}
        {isGeneratingTaglines && (
          <div className="mt-3 flex items-center gap-2 text-sm text-text-muted">
            <Loader2 className="w-4 h-4 animate-spin" />
            Generating tagline suggestions...
          </div>
        )}

        {suggestedTaglines.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-text-muted font-medium">Click to use:</p>
            {suggestedTaglines.map((tagline, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  updateState({ tagline: tagline.text });
                  setSuggestedTaglines([]);
                }}
                className={cn(
                  'w-full text-left px-4 py-3 rounded-lg border border-stone-200',
                  'bg-stone-50 hover:bg-sage-50 hover:border-sage-300',
                  'text-sm transition-colors cursor-pointer'
                )}
              >
                <span className="text-text-primary">"{tagline.text}"</span>
                {tagline.reasoning && (
                  <span className="block text-xs text-text-muted mt-1">{tagline.reasoning}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Domain */}
      <div className="space-y-3">
        <label className={labelClass}>Primary Domain</label>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="domainType"
              checked={domainType === 'subdomain'}
              onChange={() => {
                setDomainType('subdomain');
                updateState({
                  frontendDomain: `${siteId}.rumio.world`,
                  apiDomain: `api.${siteId}.rumio.world`,
                });
              }}
              className="text-sage-600"
            />
            <Globe className="w-4 h-4 text-text-muted" />
            <span>
              Use <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs font-mono">
                {subdomainPreview}
              </code>{' '}
              <span className="text-green-600 font-medium">(free)</span>
            </span>
          </label>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="domainType"
              checked={domainType === 'custom'}
              onChange={() => setDomainType('custom')}
              className="text-sage-600"
            />
            <Globe className="w-4 h-4 text-text-muted" />
            <span>I have a custom domain</span>
          </label>
        </div>

        {domainType === 'custom' && (
          <div className="ml-6 space-y-3">
            <div>
              <label className={labelClass}>Custom Domain</label>
              <input
                className={inputClass}
                value={customDomain}
                onChange={(e) => {
                  const domain = e.target.value.trim();
                  updateState({
                    frontendDomain: domain,
                    apiDomain: domain ? `api.${domain}` : '',
                  });
                }}
                placeholder="theledgerlabacademy.com"
              />
            </div>

            <div>
              <label className={labelClass}>API Domain</label>
              <div className="flex items-center gap-2">
                <Server className="w-4 h-4 text-text-muted flex-shrink-0" />
                <input
                  className={cn(inputClass, 'bg-stone-50 text-text-secondary')}
                  value={derivedApiDomain}
                  readOnly
                  tabIndex={-1}
                />
              </div>
            </div>

            <PreflightBanner
              preflight={preflight}
              pending={preflightPending}
              domain={customDomain.trim()}
            />
          </div>
        )}

        {/* Additional redirect domains */}
        <div className="mt-4">
          <label className={labelClass}>Additional Domains (redirect to primary)</label>
          <p className="text-xs text-text-muted mb-2">
            Add other domains that should redirect visitors to your main site.
          </p>
          <div className="flex gap-2">
            <input
              className={cn(inputClass, 'flex-1')}
              value={newAliasDomain}
              onChange={(e) => setNewAliasDomain(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAliasDomain(); } }}
              placeholder="ledgerlab.com"
            />
            <Button variant="outline" size="md" onClick={addAliasDomain} disabled={!newAliasDomain.trim()}>
              <Plus className="w-4 h-4" />
              Add
            </Button>
          </div>
          {getAliasDomains().length > 0 && (
            <div className="mt-2 space-y-1">
              {getAliasDomains().map((domain: string) => (
                <div key={domain} className="flex items-center gap-2 px-3 py-1.5 bg-stone-50 rounded-lg text-sm">
                  <Globe className="w-3.5 h-3.5 text-text-muted" />
                  <span className="flex-1 font-mono text-xs">{domain}</span>
                  <span className="text-text-muted">→ {state.frontendDomain || subdomainPreview}</span>
                  <button
                    onClick={() => removeAliasDomain(domain)}
                    className="text-stone-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Site administrators.
          Lifted from Step 6 (Review) on 2026-05-12 because the buried position
          there caused at least one tenant launch (threeonethreestandard) to
          ship with no end-customer admin user — the operator missed the
          tucked-away input on the final screen and the customer had no way in. */}
      <div className="space-y-3 rounded-lg border-2 border-stone-200 bg-stone-50/40 p-4">
        <div className="flex items-center gap-2">
          <UserCog className="w-4 h-4 text-sage-700" />
          <h3 className="text-sm font-semibold text-text-primary">
            Site administrators <span className="text-red-500">*</span>
          </h3>
        </div>
        <p className="text-xs text-text-muted">
          {operatorUser?.email ? (
            <>
              You (<code className="bg-stone-100 px-1 py-0.5 rounded text-xs">{operatorUser.email}</code>)
              are the platform owner and will automatically have admin access to every site.
              Add the people who should own and manage <em>this</em> site.
            </>
          ) : (
            <>Add the people who will own and manage this site.</>
          )}
        </p>

        <div className="space-y-2">
          {(state.additionalAdmins || []).length === 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              No site admins yet. If you're building this site for a customer, add their email so they can log in.
            </p>
          )}
          {(state.additionalAdmins || []).map((email: string, i: number) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  const updated = [...(state.additionalAdmins || [])];
                  updated[i] = e.target.value;
                  updateState({ additionalAdmins: updated });
                }}
                placeholder="admin@example.com"
                className={inputClass}
              />
              <button
                type="button"
                onClick={() => {
                  const updated = (state.additionalAdmins || []).filter(
                    (_: string, idx: number) => idx !== i,
                  );
                  updateState({ additionalAdmins: updated });
                }}
                className="p-2 text-red-400 hover:text-red-600 transition-colors"
                aria-label={`Remove admin ${email || i + 1}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            updateState({ additionalAdmins: [...(state.additionalAdmins || []), ''] })
          }
        >
          <Plus className="w-4 h-4" />
          Add admin
        </Button>
      </div>
    </div>
  );
}
